import React, { useState, useEffect } from 'react';
import { TextField } from '@mui/material';

import {
  BaseLabel,
  Label,
  AutoCompleteSmall,
  RowContainer,
  InputSmall,
  Select,
} from '../../../../styles/styleUtils';
import CampoMonetario from '../../components/CampoMonetario';
import { getFormasPagamento, getContasBancario, getGenericData } from '../../../api';
import { useUnidades, isSameUnidade } from '../../../../hooks/useUnidades';

function formatDateToInputValue(date) {
  const localDate = new Date(date);
  localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
  return localDate.toISOString().split('T')[0];
}

function DespesaDetails({ formData, onAutoCompleteChange, onInputChange, onInclusao }) {
  const [formas, setFormas] = useState([]);
  const [contas, setContas] = useState([]);
  const [centrocustos, setCentroCustos] = useState([]);
  const [obras, setObras] = useState([]);
  const { unidades } = useUnidades();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [formasResult, contasResult, centroCustosResult, obrasResult] = await Promise.all([
          getFormasPagamento(),
          getContasBancario(),
          getGenericData('centrocusto'),
          getGenericData('obra'),
        ]);
        setFormas(formasResult);
        setContas(contasResult);
        setCentroCustos(centroCustosResult);
        setObras(obrasResult);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      }
    };

    fetchData();
  }, []);

  const tipoDespesa = formData.tipoDespesa || 'avulsa';
  const ehParcelada = tipoDespesa === 'parcelada';
  const ehRecorrente = tipoDespesa === 'recorrente';
  const ehAgrupada = ehParcelada || ehRecorrente;

  const labelValor = ehParcelada ? 'Valor Total' : ehRecorrente ? 'Valor por Ocorrência' : 'Valor';
  const labelQuantidade = ehRecorrente ? 'Quantidade de Ocorrências' : 'Quantidade de Parcelas';

  // Na criação de série, a baixa não é informada.
  // Ao editar uma despesa existente, a baixa individual continua disponível.
  const podeInformarPagamento = !onInclusao || !ehAgrupada;

  return (
    <div>
      <RowContainer>
        <BaseLabel htmlFor="codigoDespesa">Código</BaseLabel>
        <InputSmall type="text" id="codigoDespesa" name="codigoDespesa" value={formData.codigoDespesa || ''} onChange={onInputChange} />
        <div style={{ width: 120 }} />
        <Label htmlFor="despesa">Despesa</Label>
        <InputSmall type="text" id="despesa" name="despesa" value={formData.despesa || ''} onChange={onInputChange} />
      </RowContainer>

      <RowContainer>
        <BaseLabel htmlFor="centroCusto">Centro de Custo</BaseLabel>
        <AutoCompleteSmall
          disablePortal
          id="centroCusto"
          name="centroCusto"
          value={formData.centroCusto || null}
          onChange={(_, value) => onAutoCompleteChange('idCentroCusto', 'centroCusto', value)}
          options={centrocustos}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          getOptionLabel={(option) => `${option.nome}`}
          renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
        />
        <div style={{ width: 100 }} />
        <Label htmlFor="vencimento">{ehAgrupada && onInclusao ? 'Primeiro Vencimento' : 'Vencimento'}</Label>
        <InputSmall
          type="date"
          id="vencimento"
          name="vencimento"
          value={formData.vencimento ? formatDateToInputValue(formData.vencimento) : ''}
          onChange={onInputChange}
        />
      </RowContainer>

      <RowContainer>
        <BaseLabel htmlFor="valorBaixa">{labelValor}</BaseLabel>
        <CampoMonetario name="valorBaixa" valor={formData.valorBaixa || 0} onChange={onInputChange} />
        <div style={{ width: 70 }} />

        {podeInformarPagamento && (
          <>
            <Label htmlFor="dataPagamento">Data Pagamento</Label>
            <InputSmall
              type="date"
              id="dataPagamento"
              name="dataPagamento"
              value={formData.dataPagamento ? formatDateToInputValue(formData.dataPagamento) : ''}
              onChange={onInputChange}
            />
          </>
        )}
      </RowContainer>

      {ehAgrupada && onInclusao && (
        <RowContainer>
          <BaseLabel htmlFor="quantidadeParcelas">{labelQuantidade}</BaseLabel>
          <InputSmall
            type="number"
            min="2"
            step="1"
            id="quantidadeParcelas"
            name="quantidadeParcelas"
            value={formData.quantidadeParcelas || ''}
            onChange={onInputChange}
          />
          <div style={{ width: 90 }} />
          <Label htmlFor="periodicidade">Periodicidade</Label>
          <Select id="periodicidade" name="periodicidade" value={formData.periodicidade || ''} onChange={onInputChange}>
            <option value="">Selecione</option>
            <option value="quinzenal">Quinzenal</option>
            <option value="mensal">Mensal</option>
            <option value="bimestral">Bimestral</option>
            <option value="trimestral">Trimestral</option>
            <option value="semestral">Semestral</option>
            <option value="anual">Anual</option>
          </Select>
        </RowContainer>
      )}

      {formData.dataPagamento && (
        <RowContainer>
          <BaseLabel htmlFor="formaPagamento">Forma de Pagamento</BaseLabel>
          <AutoCompleteSmall
            disablePortal
            id="formaPagamento"
            name="formaPagamento"
            value={formData.formaPagamento || null}
            onChange={(_, value) => onAutoCompleteChange('idFormaPagamento', 'formaPagamento', value)}
            options={formas}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            getOptionLabel={(option) => `${option.nome}`}
            renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
          />
          <div style={{ width: 60 }} />
          <Label htmlFor="contaPagamento">Conta Pagamento</Label>
          <AutoCompleteSmall
            disablePortal
            id="contaPagamento"
            name="contaPagamento"
            value={formData.contaPagamento || null}
            onChange={(_, value) => onAutoCompleteChange('idContaPagamento', 'contaPagamento', value)}
            options={contas}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            getOptionLabel={(option) => `${option.nome}`}
            renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
          />
        </RowContainer>
      )}

      <RowContainer>
        <BaseLabel htmlFor="unidade">Unidade</BaseLabel>
        <AutoCompleteSmall
          disablePortal
          id="unidade"
          value={formData.unidade || null}
          options={unidades}
          isOptionEqualToValue={isSameUnidade}
          getOptionLabel={(option) => `${option.nome}`}
          renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
          onChange={(_, value) => onAutoCompleteChange(null, 'unidade', value)}
          disabled={formData.idVenda}
        />
      </RowContainer>

      <RowContainer>
        <BaseLabel htmlFor="numeroDocumento">Número Documento</BaseLabel>
        <InputSmall type="text" id="numeroDocumento" name="numeroDocumento" value={formData.numeroDocumento || ''} onChange={onInputChange} />
        <div style={{ width: 150 }} />
        {formData.centroCusto?.nome === 'Obra' && (
          <>
            <Label htmlFor="obra">Obra</Label>
            <AutoCompleteSmall
              disablePortal
              id="obra"
              name="obra"
              value={formData.obra || null}
              onChange={(_, value) => onAutoCompleteChange('idObra', 'obra', value)}
              options={obras}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              getOptionLabel={(option) => `${option.nome}`}
              renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
            />
          </>
        )}
      </RowContainer>
    </div>
  );
}

export default DespesaDetails;
